import { Injectable } from '@angular/core';
import type { MapboxMap } from '@polaris-customerweb/sharedcomponents.core';
import { ConsoleLogService } from '@polaris-customerweb/sharedcomponents.shared';
import { Waypoint } from '@polaris-customerweb/sharedcomponents.user-content';
import type { Point } from 'mapbox-gl';
import { GeoJsonFeatureCollection } from 'src/app/models/geojson-feature-collection';
import { MapContentMapService } from './map-content-map.service';
import { MapService } from './map.service';
import { WaypointService } from './waypoint.service';

@Injectable({ providedIn: 'root' })
export class WaypointMapService extends MapContentMapService {
    layerIdTemp = 'tempWaypoint';

    private sourceIdTemp = 'tempWaypoint';

    constructor(
        consoleLogService: ConsoleLogService,
        mapService: MapService,
        private waypointService: WaypointService
    ) {
        super(consoleLogService, mapService);

        this.ignoreLayerIds = ['waypointsUnselectedDot', 'waypointsSelected'];
        super.setSourceId('waypoints');
    }

    override addSources(mapboxMap: MapboxMap): void {
        super.addSources(mapboxMap);

        this.mapService.addSource(mapboxMap, this.sourceIdTemp, {
            data: new GeoJsonFeatureCollection([]),
            type: 'geojson',
        });
        this.mapService.cloneLayer(
            mapboxMap,
            'waypointsSelectedWeb',
            this.layerIdTemp,
            this.sourceIdTemp
        );
        mapboxMap.setFilter(this.layerIdTemp);
    }

    getWaypointAtPoint(
        mapboxMap: MapboxMap,
        point: Point
    ): Waypoint | undefined {
        const features = this.mapService.getFeaturesAtPoint(
            mapboxMap,
            point,
            this.layerIds
        );
        const waypoint = features[0];

        if (features.length === 0) {
            return undefined;
        }

        return this.waypointService.getWaypointFromCache(
            waypoint.properties?.id as string
        );
    }

    setSourceDataFromModel(mapboxMap: MapboxMap, waypoints: Waypoint[]): void {
        const geoJson =
            waypoints.length > 0
                ? this.waypointService.waypointsToGeoJson(waypoints)
                : undefined;
        super.setSourceData(mapboxMap, geoJson);
    }

    setTempWaypoint(mapboxMap: MapboxMap, waypoint?: Waypoint | null): void {
        let geoJson: GeoJSON.Feature | GeoJSON.FeatureCollection;

        if (waypoint) {
            geoJson = waypoint.toGeoJson();
        } else {
            geoJson = new GeoJsonFeatureCollection([]);
        }

        this.mapService.setSourceData(mapboxMap, this.sourceIdTemp, geoJson);
        this.consoleLogService.debug('set temp waypoint map data');
    }

    removeTempWaypoint(mapboxMap: MapboxMap): void {
        if (this.layerIds.length === 0) {
            return;
        }

        this.setTempWaypoint(mapboxMap, null);
    }
}
